//
import stringValues from '@/shared/util/stringValues';
import type { IChildRiderStore, IChildRiderPackage } from './childriderStore.model';
import { useGlobalStore } from '@/stores/global/global.store';
import { usePolicyStore } from '@/modules/policy/store/policy.store';

export const expireDate = (state: IChildRiderStore): Date | string =>
  state.packages[0]?.crcExpireDt;

export const crcExpireDate = (state: IChildRiderStore) => {
  if (state.expirationDate instanceof Date) {
    state.expirationDate = state.expirationDate.toString();
  }
  state.expirationDate.replace(/(\d{4})-(\d{2})-(\d{2})/, '$2/$3/$1');
};

export const packagesGivenPolicyId = (state: IChildRiderStore) => (policyId: string | Date) =>
  state.packages.filter((item: IChildRiderPackage) => item.policyId === policyId);

export const hasPackageGivenPolicyId = (state: IChildRiderStore) => (policyId: string) =>
  state.packages.filter((item: IChildRiderPackage) => item.policyId === policyId).length > 0;

// export const newPolicy = (state: IChildRiderStore) => state.newPolicy;

export const NPPackage = (state: IChildRiderStore) =>
  state.packages.find(
    (item: IChildRiderPackage) =>
      item.policyId === state.newPolicy.policyId && item.packageId === state.newPolicy.packageId
  );

export const NPFirstName = (state: IChildRiderStore) => state.newPolicy.firstName;

export const NPLastName = (state: IChildRiderStore) => state.newPolicy.lastName;

export const NPFullName = (state: IChildRiderStore) =>
  `${state.newPolicy.firstName} ${state.newPolicy.lastName}`;

export const NPMiddleName = (state: IChildRiderStore) => state.newPolicy.middleName;

export const NPGender = (state: IChildRiderStore) => state.newPolicy.gender;

export const NPDOB = (state: IChildRiderStore) => state.newPolicy.DOB;

export const NPSSN = (state: IChildRiderStore) => state.newPolicy.SSN;

export const NPSelectedOption = (state: IChildRiderStore) => state.newPolicy.selectedOption;

export const NPSelectedFaceAmount = (state: IChildRiderStore) => state.newPolicy.selectedFaceAmount;

export const NPSelectedPremiumAmount = (state: IChildRiderStore) => {
  let premiumAmount = state.newPolicy.selectedPremiumAmount
    ? state.newPolicy.selectedPremiumAmount
    : '';
  const splitVal = premiumAmount.split('.');
  if (splitVal.length > 1) {
    splitVal[1] = `${splitVal[1]}00`.slice(0, 2);
    premiumAmount = `${splitVal[0]}.${splitVal[1]}`;
  } else {
    premiumAmount = `${splitVal[0]}.00`;
  }
  return premiumAmount;
};

export const NPSelectedOptionDetails = (state: IChildRiderStore) => {
  const _package = state.packages.find(
    (item: IChildRiderPackage) =>
      item.policyId === state.newPolicy.policyId && item.packageId === state.newPolicy.packageId
  );

  return _package?.selectionOptions[state.newPolicy.selectedOption];
};

export const NPEDelivery = (state: IChildRiderStore) => state.newPolicy.eDelivery;

export const insuranceSelected = (state: IChildRiderStore) => {
  const globalStore = useGlobalStore();
  const options = Object.entries(NPPackage(state)?.selectionOptions ?? {});
  let hasNoTabacoRate;
  if (!options.length) {
    hasNoTabacoRate = false;
  } else {
    // filter out the tobacco rates
    const tobaccoRates: number[] = [];
    options.forEach(([key, value]) => {
      if (key.includes('T') && typeof value === 'object' && 'premiumAmount' in value) {
        tobaccoRates.push(Number((value as { premiumAmount: string }).premiumAmount));
      }
    });
    // check if packets does not have tobacco rates (premium amounts have 0 or 0.00 values)
    hasNoTabacoRate = tobaccoRates.every((e) => e <= 0);
  }

  if (!hasNoTabacoRate) {
    const compCodes = ['04', '05', '10'];
    if (
      compCodes.some((eachCompanyCode) => NPPackage(state)?.policyCompNum.includes(eachCompanyCode))
    ) {
      return 'TermNow35';
    }
    // returns if comp code is 34
    return 'TermNow30';
  }
  const compCodes1 = ['04'];
  const compCodes2 = ['34'];
  const compCodes3 = ['05', '10'];
  if (
    compCodes1.some((eachCompanyCode) => NPPackage(state)?.policyCompNum.includes(eachCompanyCode))
  ) {
    return 'PowerTerm 35';
  } else if (
    compCodes2.some((eachCompanyCode) => NPPackage(state)?.policyCompNum.includes(eachCompanyCode))
  ) {
    return 'PowerTerm 30';
  } else if (
    compCodes3.some((eachCompanyCode) => NPPackage(state)?.policyCompNum.includes(eachCompanyCode))
  ) {
    if (globalStore.locale.valueOf() === 'fr_CA') {
      return 'Temporaire Puissance Primerica 35';
    }
    return 'Primerica PowerTerm 35';
  }
  return 'PowerTerm 35';
};

export const policyOwner = () => {
  const policyStore = usePolicyStore();
  return `${policyStore.currentPolicy?.owner?.firstName} ${policyStore.currentPolicy?.owner?.lastName}`;
};

export const bankName = () => {
  const policyStore = usePolicyStore();
  return policyStore.currentPolicy?.paymentInfo?.bank?.name;
};

export const NPAcknowledgementHTML = (state: IChildRiderStore) => {
  const policyStore = usePolicyStore();
  const rawText = state.newPolicy.acknowledgement.text;
  const interpolatedHTML: string[] = [];
  const targetName = `${state.newPolicy.firstName} ${state.newPolicy.lastName}`;
  const name = policyOwner();
  const insuranceSelection = insuranceSelected(state);

  let faceAmount = `$${state.newPolicy.selectedFaceAmount}`;
  faceAmount = faceAmount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const billForm = NPPackage(state)?.paymentForm;
  const billMode = NPPackage(state)?.paymentMode;
  let timePeriod = '3';
  if (
    (billForm === 'A1' && billMode === '1') ||
    billForm === 'C2' ||
    billForm === 'D2' ||
    billForm === 'H1'
  ) {
    timePeriod = stringValues.billingMode('3');
  } else {
    timePeriod = stringValues.billingMode(state.newPolicy.paymentMode);
  }
  let premumAmount = `$${state.newPolicy.selectedPremiumAmount}`;

  const splitVal = premumAmount.split('.');
  if (splitVal.length > 1) {
    splitVal[1] = `${splitVal[1]}00`.slice(0, 2);
    premumAmount = `${splitVal[0]}.${splitVal[1]}`;
  } else {
    premumAmount = `${splitVal[0]}.00`;
  }
  // For some reason we are reading same properties for both line # 101
  const newModePremiumAmountFormatted = premumAmount;
  const p = policyStore.policies?.[policyStore.selectedPolicyIndex];
  let dayOfMonthWith = p.paymentInfo?.autoDraftPaymentDate;
  if (dayOfMonthWith !== undefined && dayOfMonthWith.indexOf('0') === 0) {
    dayOfMonthWith = dayOfMonthWith.substring(1);
  }
  if (dayOfMonthWith === '1' || dayOfMonthWith === '21') {
    dayOfMonthWith = `${dayOfMonthWith}st`;
  } else if (dayOfMonthWith === '2' || dayOfMonthWith === '22') {
    dayOfMonthWith = `${dayOfMonthWith}nd`;
  } else if (dayOfMonthWith === '3' || dayOfMonthWith === '23') {
    dayOfMonthWith = `${dayOfMonthWith}rd`;
  } else {
    dayOfMonthWith = `${dayOfMonthWith}th`;
  }

  rawText?.forEach((item) => {
    let text = item.textContent.replace('{{ targetName }}', targetName);
    text = text.replace('{{ name }}', name);
    text = text.replace('{{ insuranceSelection }}', insuranceSelection);
    text = text.replace('{{ faceAmount }}', faceAmount);
    text = text.replace('{{ timePeriod }}', timePeriod);
    text = text.replace('{{ premumAmount }}', premumAmount);
    text = text.replace('{{newModePremiumAmountFormatted}}', newModePremiumAmountFormatted);
    text = text.replace('{{dayOfMonthWith}}', `${dayOfMonthWith}`);

    text = text.replace(/\\"|\/"/g, '');

    let _html;
    if (item.textSequence % 2 === 0) {
      _html = `<h3>${text}</h3>`;
    } else {
      _html = `<p>${text}</p>`;
    }

    // If paymentType = B2 (ACH), include sequence content, 6, 7, 8, 9
    const paymentType = NPPackage(state)?.paymentForm;
    if (
      item.textSequence === 6 ||
      item.textSequence === 7 ||
      item.textSequence === 8 ||
      item.textSequence === 9
    ) {
      if (paymentType === 'B2') {
        interpolatedHTML.push(_html);
      }
    } else {
      interpolatedHTML.push(_html);
    }
  });
  return interpolatedHTML;
};

// export const NPConfirmation = (state: IChildRiderStore) => state.newPolicyConfirmation;

// export const loading = (state: IChildRiderStore) => state.loading;

export const isUSPolicy = (state: IChildRiderStore) => NPPackage(state)?.policyCompNum === '04';

export const isCanadaPolicy = (state: IChildRiderStore) =>
  NPPackage(state)?.policyCompNum === '10' || NPPackage(state)?.policyCompNum === '05';

export const isNYPolicy = (state: IChildRiderStore) => NPPackage(state)?.policyCompNum === '34';

export const lastChild = (state: IChildRiderStore) => {
  // check lrcInd of the last child being processed for conversion
  // TODO: deal with 60 and 90 day

  // last child alert is false if there is still a valid package to be processed
  if (state.packages.length > 0) {
    return false;
  }
  return state.lrcInd === 'Y';
};
